/**
 * treeUtils.js — Pure functions for navigating and mutating a component tree.
 *
 * The component tree is an array of root component objects. Each component may
 * have a `children` array containing nested components. Components are addressed
 * by a dot-separated path string, e.g. "0.children.1.children.0" means
 * `components[0].children[1].children[0]`.
 *
 * Every function in this module is **pure** — it returns a new array/object
 * without mutating its inputs (some use JSON.parse(JSON.stringify()) for deep
 * clones where immutable traversal would be too verbose).
 */

// ---------------------------------------------------------------------------
// Navigation
// ---------------------------------------------------------------------------

/**
 * Navigate to a component in the tree using a dot-separated path.
 *
 * @param {Array} components - Root components array
 * @param {string} path - Dot-separated path, e.g. "0.children.1"
 * @returns {Object|null} Component at path, or null if not found
 */
export function getComponentAtPath(components, path) {
  if (!path || !components) return null;

  const parts = path.split('.');
  let current = components[parseInt(parts[0])];
  for (let i = 1; i < parts.length; i += 2) {
    if (parts[i] === 'children' && current?.children) {
      current = current.children[parseInt(parts[i + 1])];
    } else {
      return null;
    }
  }
  return current;
}

/**
 * Get the parent path from a component path.
 * E.g. "0.children.1.children.0" → "0.children.1"
 *
 * @param {string} path
 * @returns {string|null} Parent path, or null if root-level
 */
export function getParentPath(path) {
  if (!path) return null;
  const lastChildrenIdx = path.lastIndexOf('.children.');
  if (lastChildrenIdx === -1) return null;
  return path.substring(0, lastChildrenIdx);
}

// ---------------------------------------------------------------------------
// Structural mutations (all return new arrays)
// ---------------------------------------------------------------------------

/**
 * Immutably remove the component at `path` from the tree.
 *
 * @param {Array} components
 * @param {string} path
 * @returns {Array} New components array with the component removed
 */
export function removeComponentAtPath(components, path) {
  const parts = path.split('.');
  const index = parseInt(parts[0]);

  if (parts.length === 1) {
    const updated = [...components];
    updated.splice(index, 1);
    return updated;
  }

  const updated = [...components];
  updated[index] = { ...updated[index] };
  let current = updated[index];

  for (let i = 1; i < parts.length - 2; i += 2) {
    if (parts[i] === 'children') {
      const childIdx = parseInt(parts[i + 1]);
      current.children = [...current.children];
      current.children[childIdx] = { ...current.children[childIdx] };
      current = current.children[childIdx];
    }
  }

  const lastChildIdx = parseInt(parts[parts.length - 1]);
  current.children = [...current.children];
  current.children.splice(lastChildIdx, 1);
  return updated;
}

/**
 * Deep-clone a component, regenerating every `id` in the subtree.
 *
 * @param {Object} comp
 * @returns {Object} Cloned component with fresh IDs
 */
export function cloneWithNewIds(comp) {
  const cloned = {
    ...comp,
    id: `${comp.type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
  };
  if (comp.children?.length > 0) {
    cloned.children = comp.children.map(cloneWithNewIds);
  }
  return cloned;
}

/**
 * Insert `newComp` immediately after the component at `path`.
 *
 * @param {Array} components
 * @param {string} path
 * @param {Object} newComp
 * @returns {Array} New components array
 */
export function insertAfterPath(components, path, newComp) {
  const parts = path.split('.');
  const index = parseInt(parts[0]);

  if (parts.length === 1) {
    const updated = [...components];
    updated.splice(index + 1, 0, newComp);
    return updated;
  }

  const updated = [...components];
  updated[index] = { ...updated[index] };
  let current = updated[index];

  for (let i = 1; i < parts.length - 2; i += 2) {
    if (parts[i] === 'children') {
      const childIdx = parseInt(parts[i + 1]);
      current.children = [...current.children];
      current.children[childIdx] = { ...current.children[childIdx] };
      current = current.children[childIdx];
    }
  }

  const lastChildIdx = parseInt(parts[parts.length - 1]);
  current.children = [...current.children];
  current.children.splice(lastChildIdx + 1, 0, newComp);
  return updated;
}

/**
 * Swap a component with its sibling in the given direction.
 *
 * @param {Array} components
 * @param {string} path
 * @param {'up'|'down'} direction
 * @returns {Array} New components array (unchanged if swap is impossible)
 */
export function swapInParent(components, path, direction) {
  const parts = path.split('.');
  const currentIdx = parseInt(parts[parts.length - 1]);
  const newIdx = direction === 'up' ? currentIdx - 1 : currentIdx + 1;

  if (parts.length === 1) {
    if (newIdx < 0 || newIdx >= components.length) return components;
    const updated = [...components];
    [updated[currentIdx], updated[newIdx]] = [updated[newIdx], updated[currentIdx]];
    return updated;
  }

  const updated = [...components];
  const parentPathParts = parts.slice(0, -2);
  let parent = updated[parseInt(parentPathParts[0])];
  updated[parseInt(parentPathParts[0])] = { ...parent };
  parent = updated[parseInt(parentPathParts[0])];

  for (let i = 1; i < parentPathParts.length; i += 2) {
    if (parentPathParts[i] === 'children') {
      const childIdx = parseInt(parentPathParts[i + 1]);
      parent.children = [...parent.children];
      parent.children[childIdx] = { ...parent.children[childIdx] };
      parent = parent.children[childIdx];
    }
  }

  if (newIdx < 0 || newIdx >= parent.children.length) return components;
  parent.children = [...parent.children];
  [parent.children[currentIdx], parent.children[newIdx]] = [parent.children[newIdx], parent.children[currentIdx]];
  return updated;
}

/**
 * Move a component from one location to another (supports cross-container moves).
 *
 * Uses JSON deep-clone internally because the traversal with immutable
 * copies across two different sub-trees is prohibitively complex.
 *
 * @param {Array} components
 * @param {string} sourcePath - Path to the component being moved
 * @param {string|null} targetParentPath - Path to the new parent, or null for root
 * @param {number} targetIndex - Index within the target parent's children
 * @returns {Array} New components array (unchanged on invalid paths)
 */
export function moveComponent(components, sourcePath, targetParentPath, targetIndex) {
  const updated = JSON.parse(JSON.stringify(components));

  const componentToMove = getComponentAtPath(updated, sourcePath);
  if (!componentToMove) return components;

  const sourceParts = sourcePath.split('.');
  const sourceIndex = parseInt(sourceParts[sourceParts.length - 1]);
  const sourceIsRoot = sourceParts.length === 1;

  // Determine if same parent
  let sourceParentPath = null;
  if (!sourceIsRoot) {
    sourceParentPath = sourceParts.slice(0, -2).join('.');
  }
  const sameParent =
    sourceParentPath === targetParentPath ||
    (sourceIsRoot && targetParentPath === null);

  // Remove from source
  if (sourceIsRoot) {
    updated.splice(sourceIndex, 1);
  } else {
    const parentParts = sourceParts.slice(0, -2);
    let sourceParent = updated[parseInt(parentParts[0])];
    for (let i = 1; i < parentParts.length; i += 2) {
      if (parentParts[i] === 'children' && sourceParent?.children) {
        sourceParent = sourceParent.children[parseInt(parentParts[i + 1])];
      }
    }
    if (!sourceParent?.children) {
      return components;
    }
    sourceParent.children.splice(sourceIndex, 1);
  }

  // Adjust target index when moving within the same parent
  let adjustedTargetIndex = targetIndex;
  if (sameParent && sourceIndex < targetIndex) {
    adjustedTargetIndex = targetIndex - 1;
  }

  // Insert at target
  if (targetParentPath === null) {
    updated.splice(adjustedTargetIndex, 0, componentToMove);
  } else {
    const targetParts = targetParentPath.split('.');
    let targetParent = updated[parseInt(targetParts[0])];
    for (let i = 1; i < targetParts.length; i += 2) {
      if (targetParts[i] === 'children' && targetParent?.children) {
        targetParent = targetParent.children[parseInt(targetParts[i + 1])];
      }
    }
    if (!targetParent) {
      return components;
    }
    if (!targetParent.children) targetParent.children = [];
    targetParent.children.splice(adjustedTargetIndex, 0, componentToMove);
  }

  return updated;
}

/**
 * Immutably update the props of a component at `path`.
 *
 * @param {Array} components
 * @param {string} path
 * @param {Object} newProps - Props to merge into the component's existing props
 * @returns {Array} New components array
 */
export function updateComponentInTree(components, path, newProps) {
  const parts = path.split('.');
  const index = parseInt(parts[0]);

  if (parts.length === 1) {
    const updated = [...components];
    updated[index] = {
      ...updated[index],
      props: { ...updated[index].props, ...newProps },
    };
    return updated;
  }

  const updated = [...components];
  updated[index] = { ...updated[index] };
  let current = updated[index];

  for (let i = 1; i < parts.length; i += 2) {
    if (parts[i] === 'children' && i + 1 < parts.length) {
      const childIndex = parseInt(parts[i + 1]);
      current.children = [...current.children];

      if (i + 2 >= parts.length) {
        // Target reached
        current.children[childIndex] = {
          ...current.children[childIndex],
          props: { ...current.children[childIndex].props, ...newProps },
        };
      } else {
        current.children[childIndex] = { ...current.children[childIndex] };
        current = current.children[childIndex];
      }
    }
  }

  return updated;
}
